import type { DeepCase } from "@/types/portal";
import { deepCases } from "@/mocks/features";
import { SectionHead } from "./bits";

const statusStyle: Record<string, string> = {
  核查中: "bg-accent-100 text-accent-800",
  对簿公堂: "bg-primary-100 text-primary-700",
};

function CaseBlock({ c, index }: { c: DeepCase; index: number }) {
  const flip = index % 2 === 1;
  return (
    <div className="overflow-hidden rounded-2xl border border-background-200 bg-background-50">
      <div className="grid lg:grid-cols-12">
        <div className={`relative h-64 w-full overflow-hidden lg:col-span-5 lg:h-full ${flip ? "lg:order-2" : ""}`}>
          <img src={c.image} alt={c.title} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/50 to-transparent lg:bg-gradient-to-r" />
        </div>
        <div className="p-6 md:p-8 lg:col-span-7">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-widest text-primary-600">
              <i className="ri-focus-3-line" />
              {c.kind}
            </span>
            <span
              className={`ml-auto rounded-full px-2.5 py-1 text-xs font-medium ${statusStyle[c.status] ?? "bg-secondary-100 text-secondary-800"}`}
            >
              {c.status}
            </span>
          </div>
          <h3 className="mt-3 font-heading text-xl font-bold leading-snug text-foreground-950 md:text-2xl">
            {c.title}
          </h3>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-[15px]">{c.lead}</p>

          <ol className="mt-6 space-y-0">
            {c.timeline.map((n, i) => (
              <li key={i} className="relative flex gap-4 pb-5 last:pb-0">
                {i !== c.timeline.length - 1 ? (
                  <span className="absolute left-[5px] top-5 h-full w-px bg-background-200" />
                ) : null}
                <span className="relative mt-1.5 flex h-[11px] w-[11px] shrink-0 items-center justify-center">
                  <span
                    className={`absolute h-[11px] w-[11px] rounded-full ${
                      i === c.timeline.length - 1 ? "bg-primary-500" : "bg-background-200"
                    }`}
                  />
                  <span className="absolute h-[5px] w-[5px] rounded-full bg-background-50" />
                </span>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-en text-[11px] uppercase tracking-widest text-primary-600">
                      {n.time}
                    </span>
                  </div>
                  <p className="mt-1 text-sm font-semibold text-foreground-900">{n.title}</p>
                  <p className="mt-1 text-[13px] leading-relaxed text-foreground-600">{n.text}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );
}

export default function Evidence() {
  return (
    <section id="evidence" className="scroll-mt-24 border-y border-background-200 bg-background-100/60">
      <div className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-16">
        <SectionHead
          eyebrow="Evidence Chain"
          title="实锤证据链 · 深挖复盘"
          desc="我们把零散的黑料预告还原成可核验的时间线与证据节点，标记每一阶段的信源类型与核验状态，持续跟进直至实锤落地。"
          action={
            <div className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary-100 px-3 py-1.5 text-secondary-800">
                <i className="ri-checkbox-circle-line" /> 已证实
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-3 py-1.5 text-accent-800">
                <i className="ri-time-line" /> 待核实
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-background-200 px-3 py-1.5 text-foreground-600">
                <i className="ri-chat-quote-line" /> 单方说法
              </span>
            </div>
          }
        />
        <div className="space-y-6">
          {deepCases.map((c, i) => (
            <CaseBlock key={c.id} c={c} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}